import React, { useState, useRef } from 'react';
import { Button } from '../ui/button';
import { Download, Upload, AlertCircle, CheckCircle, Info } from 'lucide-react';
import { projectId, publicAnonKey } from '/utils/supabase/info';

interface ExportImportProps {
  getAccessToken: () => Promise<string | null>;
}

export function ExportImport({ getAccessToken }: ExportImportProps) {
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleExport = async () => {
    try {
      setExporting(true);
      setMessage(null);

      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-586f2897/export`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${publicAnonKey}`
          }
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Export failed');
      }

      const data = await response.json();
      
      // Create downloadable JSON file
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `zsalako-backup-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      const projectCount = data.data?.projects?.length || 0;
      const newsCount = data.data?.news?.length || 0;
      const teamCount = data.data?.team?.length || 0;
      const assetCount = data.data?.assets?.length || 0;

      setMessage({
        type: 'success',
        text: `Export sikeres! ${projectCount} projekt, ${newsCount} hír, ${teamCount} csapattag, ${assetCount} asset exportálva.`
      });
    } catch (error) {
      console.error('Export error:', error);
      setMessage({
        type: 'error',
        text: `Export hiba: ${(error as Error).message}`
      });
    } finally {
      setExporting(false);
    }
  };

  const handleImportClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setImporting(true);
      setMessage(null);

      // Read file content
      const fileContent = await file.text();
      const importData = JSON.parse(fileContent);

      // Validate data structure
      if (!importData.data) {
        throw new Error('Érvénytelen fájlformátum. Kérlek egy exportált backup fájlt válassz.');
      }

      const token = await getAccessToken();
      if (!token) {
        throw new Error('Nincs érvényes bejelentkezés. Kérlek jelentkezz be újra.');
      }

      // Send import request
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-586f2897/import`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${publicAnonKey}`
          },
          body: JSON.stringify(importData)
        }
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Import failed');
      }

      const result = await response.json();
      
      setMessage({
        type: 'success',
        text: `Import sikeres! ${result.imported?.projects || 0} projekt, ${result.imported?.news || 0} hír, ${result.imported?.team || 0} csapattag, ${result.imported?.assets || 0} asset importálva.`
      });

      // Clear file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

      // Reload page after 2 seconds
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Import error:', error);
      setMessage({
        type: 'error',
        text: `Import hiba: ${(error as Error).message}`
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold mb-2">Adatok exportálása és importálása</h2>
        <p className="text-gray-600 text-sm">
          Készíts biztonsági mentést az összes adatról, vagy importálj egy korábbi mentést.
        </p>
      </div>

      {message && (
        <div className={`p-4 rounded-lg border flex items-start gap-3 ${
          message.type === 'success' ? 'bg-green-50 border-green-200' :
          message.type === 'error' ? 'bg-red-50 border-red-200' :
          'bg-blue-50 border-blue-200'
        }`}>
          {message.type === 'success' && <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />}
          {message.type === 'error' && <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />}
          {message.type === 'info' && <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />}
          <p className={`text-sm ${
            message.type === 'success' ? 'text-green-800' :
            message.type === 'error' ? 'text-red-800' :
            'text-blue-800'
          }`}>
            {message.text}
          </p>
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        <div>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Download className="h-5 w-5" />
            Export (Mentés)
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Az összes adat letöltése JSON formátumban: projektek, hírek, kategóriák, csapat, rólunk szöveg, kapcsolat információk, asset-ek, beállítások.
          </p>
          <Button
            onClick={handleExport}
            disabled={exporting}
            className="w-full sm:w-auto"
          >
            <Download className="mr-2 h-4 w-4" />
            {exporting ? 'Exportálás...' : 'Adatok exportálása'}
          </Button>
        </div>

        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            <Upload className="h-5 w-5" />
            Import (Visszaállítás)
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            Egy korábbi mentés visszatöltése. <strong className="text-red-600">Figyelem:</strong> Ez felülírja a jelenlegi adatokat!
          </p>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleFileSelect}
            className="hidden"
          />
          <Button
            onClick={handleImportClick}
            disabled={importing}
            variant="outline"
            className="w-full sm:w-auto border-orange-600 text-orange-600 hover:bg-orange-50"
          >
            <Upload className="mr-2 h-4 w-4" />
            {importing ? 'Importálás...' : 'Adatok importálása'}
          </Button>
        </div>
      </div>

      <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
        <h4 className="font-semibold text-blue-900 mb-3 flex items-center gap-2">
          <Info className="h-5 w-5" />
          Fontos információk
        </h4>
        <ul className="space-y-2 text-sm text-blue-800">
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">•</span>
            <span>Az <strong>export</strong> letölti az összes szöveges adatot és a képek URL-jeit</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">•</span>
            <span>A képek fizikai fájljai <strong>nem</strong> kerülnek be a JSON-be, csak az URL-ek</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">•</span>
            <span>Ha másik Supabase account-ra költözöl, a képeket <strong>manuálisan</strong> kell feltölteni az új storage-ba</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">•</span>
            <span>Az <strong>import</strong> visszaállítja az adatokat, de a képek URL-jeit frissíteni kell az új storage URL-ekre</span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">•</span>
            <span>Javasolt rendszeres backup készítés (hetente/havonta)</span>
          </li>
        </ul>
      </div>

      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
        <h4 className="font-semibold text-yellow-900 mb-3 flex items-center gap-2">
          <AlertCircle className="h-5 w-5" />
          Képek mentése másik Supabase-re költözéshez
        </h4>
        <div className="space-y-3 text-sm text-yellow-800">
          <p>Ha másik Supabase account-ra szeretnéd átvinni a projekteket:</p>
          <ol className="list-decimal list-inside space-y-2 ml-2">
            <li>Készíts <strong>export</strong>-ot (mentsd el a JSON fájlt)</li>
            <li>Menj a jelenlegi Supabase Dashboard → Storage → minden bucket-re:
              <ul className="list-disc list-inside ml-6 mt-1 space-y-1">
                <li><code className="bg-yellow-100 px-1 py-0.5 rounded text-xs">make-586f2897-projects</code></li>
                <li><code className="bg-yellow-100 px-1 py-0.5 rounded text-xs">make-586f2897-news-covers</code></li>
                <li><code className="bg-yellow-100 px-1 py-0.5 rounded text-xs">make-586f2897-about-images</code></li>
                <li><code className="bg-yellow-100 px-1 py-0.5 rounded text-xs">make-586f2897-team-photos</code></li>
                <li><code className="bg-yellow-100 px-1 py-0.5 rounded text-xs">make-586f2897-assets</code></li>
              </ul>
            </li>
            <li>Töltsd le az összes képet minden bucket-ből</li>
            <li>Az új Supabase-ben töltsd fel a képeket ugyanazokba a bucket-ekbe</li>
            <li>Importáld a JSON fájlt az új környezetbe</li>
            <li>Szükség esetén frissítsd a kép URL-eket az admin felületen</li>
          </ol>
        </div>
      </div>
    </div>
  );
}